/*
Copyright 2026 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import {
  type ChangeEvent,
  type FC,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
  Root as Form,
  InlineField,
  Label,
  RadioControl,
  Text,
} from "@vector-im/compound-web";
import { logger } from "matrix-js-sdk/lib/logger";

import {
  backgroundBlurLevels,
  type BackgroundBlurLevel,
} from "../livekit/backgroundBlur";
import {
  builtInBackgrounds,
  CUSTOM_BACKGROUND,
  NO_BACKGROUND,
} from "../livekit/backgroundImages";
import {
  clearCustomBackground,
  loadCustomBackground,
  saveCustomBackground,
  storageSupported,
} from "../livekit/customBackground";
import { useTrackProcessor } from "../livekit/TrackProcessorContext";
import {
  backgroundBlur as backgroundBlurSetting,
  backgroundImage as backgroundImageSetting,
  useSetting,
} from "./settings";

/**
 * Выбор фона: ничего, размытие или картинка.
 *
 * Выключение — такой же пункт списка, как и остальные: раньше здесь стоял
 * выключатель с единственной силой размытия, и человеку, которому она мылила
 * лицо слишком сильно или, наоборот, не прятала комнату, оставалось только
 * выключить размытие целиком.
 *
 * Картинка добавлена по просьбе CTO 14.09.2026. Список один на размытие и на
 * картинки: это взаимоисключающие вещи, и двумя списками человек мог бы выбрать
 * обе сразу, а увидел бы только одну.
 */
export const BackgroundSelection: FC = () => {
  const { t } = useTranslation();
  const { supported } = useTrackProcessor();
  const [blurLevel, setBlurLevel] = useSetting(backgroundBlurSetting);
  const [image, setImage] = useSetting(backgroundImageSetting);
  const groupId = useId();
  const fileInput = useRef<HTMLInputElement | null>(null);
  const [hasOwn, setHasOwn] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Есть ли уже отложенная своя картинка — спрашиваем один раз при открытии
  // окна. Чтение из хранилища — действие, а не расчёт, поэтому эффектом:
  // в самой отрисовке ему делать нечего.
  useEffect(() => {
    let dropped = false;
    loadCustomBackground()
      .then((blob) => {
        if (!dropped) setHasOwn(blob !== null);
      })
      .catch((e) => logger.error("Не удалось узнать про свою картинку", e));
    return (): void => {
      dropped = true;
    };
  }, []);

  // Ключи перечислены поимённо, а не собраны из уровня: сборщик переводов
  // (i18next-parser) читает исходник статически и вычисленный ключ просто не
  // увидит — строки молча выпали бы из словарей.
  const blurLabel = (level: BackgroundBlurLevel): string => {
    switch (level) {
      case "off":
        return t("settings.background_blur_off");
      case "weak":
        return t("settings.background_blur_weak");
      case "medium":
        return t("settings.background_blur_medium");
      case "strong":
        return t("settings.background_blur_strong");
    }
  };

  const imageLabel = (id: string): string => {
    switch (id) {
      case "light":
        return t("settings.background_image_light");
      case "dark":
        return t("settings.background_image_dark");
      case "teal":
        return t("settings.background_image_teal");
      default:
        return id;
    }
  };

  /** Размытие и картинка исключают друг друга: выбор один. */
  const chooseBlur = useCallback(
    (level: BackgroundBlurLevel): void => {
      setBlurLevel(level);
      setImage(NO_BACKGROUND);
      setError(null);
    },
    [setBlurLevel, setImage],
  );

  const chooseImage = useCallback(
    (id: string): void => {
      setBlurLevel("off");
      setImage(id);
      setError(null);
    },
    [setBlurLevel, setImage],
  );

  const onFile = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      const file = event.target.files?.[0];
      // Поле очищаем сразу: иначе выбор того же файла второй раз не сработает —
      // значение не изменилось, и события не будет.
      event.target.value = "";
      if (!file) return;

      saveCustomBackground(file)
        .then(() => {
          setHasOwn(true);
          chooseImage(CUSTOM_BACKGROUND);
        })
        .catch((e) => {
          logger.error("Не удалось сохранить свою картинку фона", e);
          setError(t("settings.background_image_failed"));
        });
    },
    [chooseImage, t],
  );

  const forgetOwn = useCallback((): void => {
    void clearCustomBackground().then(() => {
      setHasOwn(false);
      if (image === CUSTOM_BACKGROUND) chooseImage(NO_BACKGROUND);
    });
  }, [chooseImage, image]);

  const selected = blurLevel !== "off" ? `blur:${blurLevel}` : `image:${image}`;

  return (
    <>
      <h4>{t("settings.background_blur_header")}</h4>

      {/* Список обязан лежать внутри Form: поля Compound без него падают на
          первой же отрисовке — окно настроек просто не открывалось бы. */}
      <Form>
        {backgroundBlurLevels.map((level) => (
          <InlineField
            key={`blur:${level}`}
            name={groupId}
            control={
              <RadioControl
                checked={
                  level === "off"
                    ? selected === "image:"
                    : selected === `blur:${level}`
                }
                value={`blur:${level}`}
                disabled={!supported}
                onChange={(): void => chooseBlur(level)}
              />
            }
          >
            <Label>{blurLabel(level)}</Label>
          </InlineField>
        ))}

        {builtInBackgrounds.map((background) => (
          <InlineField
            key={`image:${background.id}`}
            name={groupId}
            control={
              <RadioControl
                checked={selected === `image:${background.id}`}
                value={`image:${background.id}`}
                disabled={!supported}
                onChange={(): void => chooseImage(background.id)}
              />
            }
          >
            <Label>{imageLabel(background.id)}</Label>
          </InlineField>
        ))}

        {hasOwn && (
          <InlineField
            name={groupId}
            control={
              <RadioControl
                checked={selected === `image:${CUSTOM_BACKGROUND}`}
                value={`image:${CUSTOM_BACKGROUND}`}
                disabled={!supported}
                onChange={(): void => chooseImage(CUSTOM_BACKGROUND)}
              />
            }
          >
            <Label>{t("settings.background_image_own")}</Label>
          </InlineField>
        )}
      </Form>

      {supported && storageSupported() && (
        <>
          <input
            ref={fileInput}
            type="file"
            accept="image/*"
            hidden
            onChange={onFile}
            data-testid="background-file"
          />
          <button
            type="button"
            onClick={(): void => fileInput.current?.click()}
            data-testid="background-choose-own"
          >
            {t("settings.background_image_choose")}
          </button>
          {hasOwn && (
            <button
              type="button"
              onClick={forgetOwn}
              data-testid="background-forget-own"
            >
              {t("settings.background_image_forget")}
            </button>
          )}
        </>
      )}

      {error !== null && <Text size="sm">{error}</Text>}
      {!supported && (
        <Text size="sm">{t("settings.blur_not_supported_by_browser")}</Text>
      )}
    </>
  );
};
